import { useEffect } from "react";
import { useWishlistStore } from "@/store/wishlistStore";
import { useCartStore } from "@/store/cartStore";
import ShopifyProductCard from "./ShopifyProductCard";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Heart, ShoppingCart, Loader2 } from "lucide-react";
import { alertToast } from "@/contexts/AlertToastContext";

const FavoritesSection = () => {
  const { items, isLoading, loadWishlist, clearWishlist, removeItem } = useWishlistStore();
  const addToCart = useCartStore((state) => state.addItem);

  useEffect(() => {
    loadWishlist();
  }, [loadWishlist]);

  const handleAddAllToCart = async () => {
    if (items.length === 0) return;
    
    try {
      let addedCount = 0;
      
      for (const product of items) {
        const defaultVariant = product.variants?.edges?.[0]?.node;
        
        if (defaultVariant && defaultVariant.availableForSale) {
          addToCart({
            product: product,
            variantId: defaultVariant.id,
            variantTitle: defaultVariant.title,
            price: {
              amount: defaultVariant.price.amount,
              currencyCode: defaultVariant.price.currencyCode,
            },
            quantity: 1,
          });
          
          // Remove from wishlist
          await removeItem(product.id);
          addedCount++;
        }
      }
      
      if (addedCount > 0) {
        alertToast.success(`${addedCount} מוצרים הועברו לסל הקניות!`);
      }
    } catch (error) {
      console.error('Error adding all to cart:', error);
      alertToast.error('שגיאה בהעברת המוצרים לסל');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="gold-bar-loader" />
          <span className="text-sm text-muted-foreground">טוען מועדפים...</span>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-right font-light">המועדפים שלי</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Heart className="h-16 w-16 text-muted-foreground/40 mx-auto mb-6" />
            <h3 className="text-xl font-serif mb-2">אין לך מוצרים מועדפים</h3>
            <p className="text-muted-foreground mb-6">
              התחל לסמן מוצרים שאתה אוהב כדי למצוא אותם בקלות כאן
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-right font-light">המועדפים שלי</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleAddAllToCart}
                size="sm"
                className="gap-2"
              >
                <ShoppingCart className="h-4 w-4" />
                העבר הכל לסל
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearWishlist}
              >
                נקה הכל
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-6">
            {items.length} {items.length === 1 ? 'מוצר מועדף' : 'מוצרים מועדפים'}
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((product) => (
              <ShopifyProductCard key={product.id} product={product} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FavoritesSection;
